import { useCallback, useMemo } from 'react';
import { getText } from 'i18n/intl';
import isEmpty from 'lodash/isEmpty';
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  isSavingPathwayConfigsSelector,
  mappingChangesSelector,
  pathwayConfigsSelector,
  setMappingChanges,
  updatePathwayConfigs,
} from 'slices/studyCombinations.slice';
import type { TPathwayConfigFromUser } from '@timeedit/types/lib/types/pathwayConfigFromUser.type';
import { Button, DefaultContentLayout } from '@timeedit/ui-components';
import { StudyCombinationsMappingTable } from './StudyCombinationsMappingTable';

export type TMappingType =
  | 'primaryObject'
  | 'activityType'
  | 'expectedEnrolment'
  | 'trackMaxCapacity';

// Mapping from pathway config ID => mapping type => extId
export type TMappings = Record<string, Partial<Record<TMappingType, string>>>;

const StudyCombinationsMappingPage = () => {
  const dispatch = useAppDispatch();

  const isSavingPathwayConfigs = useAppSelector(isSavingPathwayConfigsSelector);
  const mappingChanges = useAppSelector(mappingChangesSelector);
  const pathwayConfigs = useAppSelector(pathwayConfigsSelector);

  const hasChangedMappings = useMemo(
    () => !isEmpty(mappingChanges),
    [mappingChanges]
  );

  const saveMappingChanges = useCallback(() => {
    const pathwayConfig: Partial<TPathwayConfigFromUser>[] = Object.entries(
      mappingChanges
    ).map(([pathwayConfigId, newMapping]) => {
      const originalPathwayConfig = pathwayConfigs.find(
        (config) => config._id.toString() === pathwayConfigId
      );

      const originalMapping = originalPathwayConfig?.activityValuesMapping;

      return {
        _id: pathwayConfigId as any,
        // Merge with original mapping to keep those values
        activityValuesMapping: {
          primaryObjectExtId: Object.hasOwn(newMapping, 'primaryObject')
            ? newMapping.primaryObject
            : originalMapping?.primaryObjectExtId,
          activityTypeExtId: Object.hasOwn(newMapping, 'activityType')
            ? newMapping.activityType
            : originalMapping?.activityTypeExtId,
          numberOfStudentsExtId: Object.hasOwn(newMapping, 'expectedEnrolment')
            ? newMapping.expectedEnrolment
            : originalMapping?.numberOfStudentsExtId,
          trackCapacityExtId: Object.hasOwn(newMapping, 'trackMaxCapacity')
            ? newMapping.trackMaxCapacity
            : originalMapping?.trackCapacityExtId,
        },
      };
    });

    dispatch(updatePathwayConfigs(pathwayConfig));
  }, [dispatch, mappingChanges, pathwayConfigs]);

  const discardChanges = useCallback(() => {
    dispatch(setMappingChanges({}));
  }, [dispatch]);

  return (
    <DefaultContentLayout
      title={getText('data_manager.study_combinations_mapping.page.title')}
      subtitle={getText(
        'data_manager.study_combinations_mapping.page.subtitle'
      )}
      actions={[
        <Button
          key="discard_changes"
          disabled={!hasChangedMappings || isSavingPathwayConfigs}
          onClick={discardChanges}
        >
          {getText(
            'data_manager.study_combinations_mapping.page.actions.discard_changes'
          )}
        </Button>,
        <Button
          key="save_changes"
          type="primary"
          loading={isSavingPathwayConfigs}
          disabled={!hasChangedMappings}
          onClick={saveMappingChanges}
        >
          {getText('data_manager.study_combinations_mapping.page.actions.save')}
        </Button>,
      ]}
    >
      <StudyCombinationsMappingTable />
    </DefaultContentLayout>
  );
};

export default StudyCombinationsMappingPage;
